/**
 * Fetch utilities with timeout and error handling
 */

export const DEFAULT_TIMEOUT = 30000; // 30 seconds
export const RETRY_ATTEMPTS = 3;
export const RETRY_DELAY = 1000; // 1 second

/**
 * Fetch with timeout using AbortController
 */
export function fetchWithTimeout(
  url: string | URL | Request, 
  options: RequestInit = {}, 
  timeout: number = DEFAULT_TIMEOUT
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  return fetch(url, {
    ...options,
    signal: controller.signal
  }).finally(() => {
    clearTimeout(timeoutId);
  });
}

/**
 * Fetch with retry logic and timeout
 */
export async function fetchWithRetry(
  url: string | URL | Request,
  options: RequestInit = {},
  maxRetries: number = RETRY_ATTEMPTS,
  timeout: number = DEFAULT_TIMEOUT
): Promise<Response> {
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔄 Fetch attempt ${attempt}/${maxRetries} for:`, url);
      
      const response = await fetchWithTimeout(url, options, timeout);
      
      // If response is ok, return it
      if (response.ok) {
        console.log(`✅ Fetch successful on attempt ${attempt}`);
        return response;
      }
      
      // If it's a client error (4xx), don't retry
      if (response.status >= 400 && response.status < 500) {
        throw new Error(`Client error: ${response.status} ${response.statusText}`);
      }
      
      // For server errors (5xx), we'll retry
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
      
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown fetch error');
      
      console.warn(`❌ Fetch attempt ${attempt} failed:`, lastError.message);
      
      // If it's an AbortError (timeout), don't retry immediately
      if (lastError.name === 'AbortError') {
        console.warn(`⏱️ Request timed out after ${timeout}ms`);
      }
      
      // If this is the last attempt, throw the error
      if (attempt === maxRetries) {
        console.error(`💥 All ${maxRetries} fetch attempts failed`);
        throw lastError;
      }
      
      // Wait before retrying (exponential backoff)
      const delay = RETRY_DELAY * Math.pow(2, attempt - 1);
      console.log(`⏳ Waiting ${delay}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError!;
}

/**
 * Enhanced fetch for Supabase with better error handling
 */
export function createSupabaseFetch(timeout: number = DEFAULT_TIMEOUT) {
  return async (url: string | URL | Request, options: RequestInit = {}): Promise<Response> => {
    try {
      // Log the request for debugging
      console.log('🚀 Supabase request:', {
        url: url.toString(),
        method: options.method || 'GET',
        timeout
      });
      
      const response = await fetchWithTimeout(url, options, timeout);
      
      // Log successful responses
      console.log('✅ Supabase response:', {
        status: response.status,
        statusText: response.statusText,
        url: url.toString()
      });
      
      return response;
      
    } catch (error) {
      // Enhanced error logging
      console.error('❌ Supabase fetch error:', {
        url: url.toString(),
        error: error instanceof Error ? error.message : 'Unknown error',
        name: error instanceof Error ? error.name : 'Unknown',
        timeout
      });
      
      // Re-throw with more context
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error(`Request timeout after ${timeout}ms: ${url.toString()}`);
        }
        throw error;
      }
      
      throw new Error(`Network error: ${url.toString()}`);
    }
  };
}